import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Microphone, Camera, Trash, Plus, FileText, MagicWand, Check, Stop, Image as ImageIcon, ShieldCheck, FolderOpen } from "@phosphor-icons/react";
import { DisclaimerBanner, DisclaimerModal } from "../components/Disclaimer";
import { AIDisclaimer } from "../components/AIDisclaimer";

const genId = () => (globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2));

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

/**
 * Mobile-safe image preview source.
 * Some Android browsers stall when rendering large base64 data URLs as
 * <img src>. We compute a same-origin blob URL that the browser can render
 * instantly while keeping the base64 payload for the server.
 */
function photoPreviewSrc(photo) {
  if (photo.previewUrl) return photo.previewUrl;
  return photo.data;
}

function MicCaptionText({ transcribing, recording }) {
  if (transcribing) return "Trascrizione…";
  if (recording) return "Registrazione… tocca per fermare";
  return "Tocca per parlare";
}

function VoiceRecorder({ transcript, setTranscript }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "audio.webm");
          const { data } = await api.post("/quotes/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setTranscript((p) => (p ? p + " " : "") + (data.text || ""));
          toast.success("Trascrizione completata");
        } catch (e) {
          toast.error("Errore trascrizione: " + (e?.response?.data?.detail || e.message));
        } finally { setTranscribing(false); }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Microfono non disponibile");
    }
  };

  const stopRec = () => {
    mediaRef.current?.stop();
    setRecording(false);
  };

  const btnClass = recording ? "bg-[#FF3B30] mic-recording" : "bg-[#FFD600] hover:bg-[#E6C200]";

  return (
    <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-6 mb-4">
      <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold mb-4">1 · Descrivi vocalmente</div>
      <div className="flex flex-col items-center gap-4">
        <button data-testid="mic-button"
          onClick={recording ? stopRec : startRec}
          disabled={transcribing}
          className={`w-32 h-32 rounded-full flex items-center justify-center transition ${btnClass} disabled:opacity-60`}>
          {recording ? <Stop size={56} weight="fill" className="text-white" /> : <Microphone size={56} weight="fill" className="text-[#0A0A0A]" />}
        </button>
        <div className="text-sm font-bold uppercase tracking-wider text-[#A3A3A3]">
          <MicCaptionText transcribing={transcribing} recording={recording} />
        </div>
      </div>
      <textarea data-testid="transcript-area" value={transcript} onChange={(e)=>setTranscript(e.target.value)}
        placeholder="Es. Devo fare spicconamento intonaco, mettere rete e intonaco da 2cm su 30 metri quadri…"
        className="w-full mt-4 bg-[#0A0A0A] border-2 border-[#333] rounded-md p-3 text-base text-white min-h-[100px] focus:border-[#FFD600] focus:outline-none" />
    </div>
  );
}

/**
 * Group items by their section field, preserving first-occurrence order.
 * Returns: [{section: "Bagno", items: [...]}, {section: "", items: [...]}]
 */
function groupBySection(items) {
  const groups = [];
  const map = new Map();
  items.forEach((it) => {
    const sec = (it.section || "").trim();
    if (!map.has(sec)) {
      map.set(sec, groups.length);
      groups.push({ section: sec, items: [] });
    }
    groups[map.get(sec)].items.push(it);
  });
  return groups;
}


function ItemRow({ item, idx, onChange, onRemove }) {
  const lineTotal = (Number(item.quantity) || 0) * (Number(item.price) || 0);
  return (
    <div className="bg-[#0A0A0A] border border-[#333] rounded-md p-3">
      <div className="flex justify-between gap-2">
        <div className="flex-1">
          <div className="font-mono text-xs text-[#FFD600]">{item.code}</div>
          <div className="text-sm font-bold">{item.description}</div>
        </div>
        <button onClick={onRemove} data-testid={`remove-item-${idx}`} className="text-[#FF3B30] p-1"><Trash size={20} weight="bold"/></button>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold">Q.tà</label>
          <input data-testid={`item-qty-${idx}`} type="number" step="0.01" value={item.quantity} onChange={(e)=>onChange("quantity", e.target.value)}
            className="w-full bg-[#1A1A1A] border border-[#333] rounded h-10 px-2 text-base focus:border-[#FFD600] focus:outline-none" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold">U.M.</label>
          <div className="h-10 flex items-center px-2 text-base text-[#A3A3A3]">{item.unit}</div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold">€/u</label>
          <input data-testid={`item-price-${idx}`} type="number" step="0.01" value={item.price} onChange={(e)=>onChange("price", e.target.value)}
            className="w-full bg-[#1A1A1A] border border-[#333] rounded h-10 px-2 text-base focus:border-[#FFD600] focus:outline-none" />
        </div>
      </div>
      <div className="mt-2 text-right font-mono text-[#FFD600] font-bold">€ {lineTotal.toFixed(2)}</div>
    </div>
  );
}

export default function NewQuote() {
  const nav = useNavigate();
  const { id: editId } = useParams();  // /preventivi/:id/modifica
  const [searchParams] = useSearchParams();
  const fromComputo = searchParams.get("from_computo");  // computo run id
  const [transcript, setTranscript] = useState("");
  const [photos, setPhotos] = useState([]);
  const [items, setItems] = useState([]);
  const [pricelist, setPricelist] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [client, setClient] = useState("");
  const [site, setSite] = useState("");
  const [aiNotes, setAiNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [loadedQuoteNumber, setLoadedQuoteNumber] = useState(null);
  const isEdit = Boolean(editId);
  const [photoConsent, setPhotoConsent] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("etabuild_photo_gdpr_v1") === "1";
  });

  const grantPhotoConsent = () => {
    localStorage.setItem("etabuild_photo_gdpr_v1", "1");
    setPhotoConsent(true);
  };

  const loadPricelist = useCallback(async () => {
    try {
      const { data } = await api.get("/pricelist");
      setPricelist([...data.default, ...data.custom]);
    } catch {
      // Silent fail: pricelist non disponibile = utente userà solo AI/manuale
      toast.error("Listino temporaneamente non disponibile");
    }
  }, []);
  useEffect(() => { loadPricelist(); }, [loadPricelist]);

  // Edit mode: load an existing quote into the form so the user can tweak
  // items, change prices, add photos, etc.
  useEffect(() => {
    if (!editId) return;
    api.get(`/quotes/${editId}`).then(({ data }) => {
      setLoadedQuoteNumber(data.number);
      setClient(data.client_name || "");
      setSite(data.site_address || "");
      setTranscript(data.description || "");
      setItems((data.items || []).map((it) => ({ ...it, _id: genId() })));
      setPhotos((data.photos || []).map((ph) => ({ ...ph, _id: genId() })));
    }).catch(() => toast.error("Preventivo non trovato"));
  }, [editId]);

  // From-Computo mode: materialise the computo run into a quote on the
  // backend and then redirect to the edit page so the user can refine it.
  useEffect(() => {
    if (!fromComputo || editId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/computo/${fromComputo}/to-quote`);
        if (cancelled) return;
        toast.success(`Preventivo creato dal computo (${data.items_count} voci)`);
        nav(`/preventivi/${data.id}/modifica`, { replace: true });
      } catch (err) {
        if (cancelled) return;
        toast.error(err?.response?.data?.detail || "Impossibile generare il preventivo");
        nav("/computo", { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [fromComputo, editId, nav]);

  const onPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      const data = await fileToBase64(f);
      setPhotos((p) => [...p, { _id: genId(), data, caption: "" }]);
    }
    e.target.value = "";
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((ph) => ph._id !== id));

  const analyze = async () => {
    if (!transcript.trim() && photos.length === 0) return toast.error("Aggiungi descrizione o foto");
    setAnalyzing(true);
    try {
      const { data } = await api.post("/quotes/analyze", { transcript, photos: photos.map(({ data, caption }) => ({ data, caption })) });
      const withIds = (data.items || []).map((it) => ({ ...it, _id: genId() }));
      // Append mode: never wipe items the user already has — voice in edit
      // mode (or after a previous analyze run) must add new rows, not reset.
      setItems((prev) => prev.length === 0 ? withIds : [...prev, ...withIds]);
      setAiNotes(data.ai_notes || "");
      if (!withIds.length) toast.info("Nessuna voce trovata, aggiungile manualmente");
      else if (items.length === 0) toast.success(`${withIds.length} voci trovate`);
      else toast.success(`${withIds.length} voci aggiunte (totale ${items.length + withIds.length})`);
    } catch {
      toast.error("Errore analisi");
    } finally { setAnalyzing(false); }
  };

  const updateItem = (id, field, value) => {
    const parsed = field === "quantity" || field === "price" ? parseFloat(value) || 0 : value;
    setItems((arr) => arr.map((it) => it._id === id ? { ...it, [field]: parsed } : it));
  };
  const removeItem = (id) => setItems((a) => a.filter((it) => it._id !== id));

  const addManual = (item) => setItems((a) => [...a, { ...item, quantity: 1, _id: genId() }]);

  const total = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.price) || 0), 0);

  const save = async () => {
    if (items.length === 0) return toast.error("Aggiungi almeno una voce");
    setSaving(true);
    try {
      const stripped = items.map(({ _id, confidence, match_reason, ...rest }) => rest);
      const photoPayload = photos.map(({ data, caption }) => ({ data, caption }));
      if (isEdit) {
        await api.put(`/quotes/${editId}`, {
          client_name: client, site_address: site, description: transcript,
          items: stripped, photos: photoPayload,
        });
        toast.success("Preventivo aggiornato");
        nav(`/preventivi/${editId}`);
      } else {
        const { data } = await api.post("/quotes", {
          client_name: client, site_address: site, description: transcript,
          items: stripped, photos: photoPayload,
        });
        toast.success("Preventivo creato");
        nav(`/preventivi/${data.id}`);
      }
    } catch {
      toast.error("Errore salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <Layout>
      <DisclaimerModal />
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold flex items-center gap-2">
          Preventivo
          {fromComputo && !editId && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FFD600] text-[#0A0A0A] font-bold flex items-center gap-1">
              Generazione in corso…
            </span>
          )}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-none mt-1">
          {isEdit ? "Modifica" : "Nuovo Lavoro"}
        </h1>
        {isEdit && loadedQuoteNumber && (
          <div className="text-sm text-[#A3A3A3] mt-1 font-mono">#{loadedQuoteNumber}</div>
        )}
      </div>

      <DisclaimerBanner />

      <VoiceRecorder transcript={transcript} setTranscript={setTranscript} />

      {/* Photos */}
      <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-6 mb-4">
        <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold mb-3">2 · Foto cantiere</div>

        {!photoConsent ? (
          <div data-testid="photo-gdpr-card" className="bg-[#0A0A0A] border-2 border-[#FFD600]/40 rounded-md p-4">
            <div className="flex items-start gap-3 mb-3">
              <ShieldCheck size={28} weight="fill" className="text-[#FFD600] shrink-0 mt-0.5" />
              <div>
                <div className="font-heading text-base font-black uppercase">Tutela della privacy</div>
                <p className="text-xs text-[#A3A3A3] mt-1.5 leading-snug">
                  Le foto del cantiere sono trattate <b className="text-white">esclusivamente</b> per generare
                  il preventivo (analisi AI di lavorazioni e overlay tecnici). Sono <b className="text-white">archiviate
                  in modo cifrato</b>, accessibili solo dal tuo account e <b className="text-white">non cedute
                  a terzi</b>. Conformi al <a href="/privacy" target="_blank" rel="noreferrer" className="text-[#FFD600] underline">GDPR</a>.
                </p>
              </div>
            </div>
            <button data-testid="photo-gdpr-accept" onClick={grantPhotoConsent}
              className="w-full bg-[#FFD600] text-[#0A0A0A] h-12 rounded-md font-black uppercase text-sm hover:bg-[#E6C200] transition flex items-center justify-center gap-2">
              <Check size={18} weight="bold"/> Ho letto e accetto
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <label data-testid="photo-camera-label"
              className="flex flex-col items-center justify-center gap-1 bg-[#0A0A0A] border-2 border-dashed border-[#333] hover:border-[#FFD600] rounded-md h-24 cursor-pointer transition px-2">
              <Camera size={26} weight="bold" className="text-[#FFD600]" />
              <span className="font-heading text-sm font-black uppercase leading-tight">Scatta foto</span>
              <input type="file" accept="image/*" capture="environment" multiple onChange={onPhoto}
                className="hidden" data-testid="photo-camera-input" />
            </label>
            <label data-testid="photo-gallery-label"
              className="flex flex-col items-center justify-center gap-1 bg-[#0A0A0A] border-2 border-dashed border-[#333] hover:border-[#FFD600] rounded-md h-24 cursor-pointer transition px-2">
              <ImageIcon size={26} weight="bold" className="text-[#FFD600]" />
              <span className="font-heading text-sm font-black uppercase leading-tight">Da galleria</span>
              <input type="file" accept="image/*" multiple onChange={onPhoto}
                className="hidden" data-testid="photo-gallery-input" />
            </label>
          </div>
        )}

        {photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mt-3" data-testid="photo-thumbs">
            {photos.map((p, i) => (
              <div key={p._id} className="relative group">
                <img src={photoPreviewSrc(p)} alt="" loading="lazy"
                  className="w-full h-24 object-cover rounded-md border border-[#333] bg-[#0A0A0A]" />
                <button onClick={()=>removePhoto(p._id)} data-testid={`remove-photo-${i}`}
                  className="absolute top-1 right-1 bg-[#FF3B30] rounded-md p-1.5 text-white">
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}

        {photoConsent && <AIDisclaimer />}
      </div>

      {/* Analyze */}
      <button data-testid="analyze-btn" onClick={analyze} disabled={analyzing}
        className="w-full bg-[#FFD600] text-[#0A0A0A] h-16 text-xl font-black uppercase rounded-md mb-4 hover:bg-[#E6C200] transition disabled:opacity-50 flex items-center justify-center gap-2">
        <MagicWand size={26} weight="fill" /> {analyzing ? "Analisi AI…" : "Analizza con AI"}
      </button>

      {/* Items */}
      <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-6 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">3 · Computo metrico</div>
          <button data-testid="add-manual-btn" onClick={()=>setShowPicker(true)} className="flex items-center gap-1 text-[#FFD600] text-sm font-bold uppercase">
            <Plus size={18} weight="bold" /> Aggiungi
          </button>
        </div>
        {aiNotes && <div className="text-xs text-[#A3A3A3] mb-3 italic">{aiNotes}</div>}
        {items.length === 0 ? (
          <div className="text-[#A3A3A3] text-sm">Nessuna voce. Usa l'AI o aggiungi manualmente.</div>
        ) : (
          <div className="space-y-4">
            {groupBySection(items).map((group, gi) => (
              <div key={`${group.section}-${gi}`} className="space-y-2" data-testid={`section-${group.section || "default"}`}>
                {group.section && (
                  <div className="flex items-center gap-2 mt-2">
                    <div className="h-px flex-1 bg-[#FFD600]/30" />
                    <div className="text-xs font-black uppercase tracking-[0.2em] text-[#FFD600] px-2">
                      {group.section}
                    </div>
                    <div className="h-px flex-1 bg-[#FFD600]/30" />
                  </div>
                )}
                {group.items.map((it) => {
                  const globalIdx = items.findIndex((x) => x._id === it._id);
                  return (
                    <ItemRow key={it._id} item={it} idx={globalIdx}
                      onChange={(f, v) => updateItem(it._id, f, v)}
                      onRemove={() => removeItem(it._id)} />
                  );
                })}
              </div>
            ))}
          </div>
        )}
        {items.length > 0 && (
          <div className="mt-4 pt-4 border-t border-[#333] flex justify-between items-center">
            <div className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold">Totale</div>
            <div className="font-heading text-3xl font-black text-[#FFD600]" data-testid="quote-total">€ {total.toFixed(2)}</div>
          </div>
        )}
      </div>

      {/* Cliente */}
      <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-6 mb-4 space-y-3">
        <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">4 · Cliente / Cantiere</div>
        <input data-testid="client-name" value={client} onChange={(e)=>setClient(e.target.value)} placeholder="Nome cliente"
          className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 text-base focus:border-[#FFD600] focus:outline-none" />
        <input data-testid="site-address" value={site} onChange={(e)=>setSite(e.target.value)} placeholder="Indirizzo cantiere"
          className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 text-base focus:border-[#FFD600] focus:outline-none" />
      </div>

      <button data-testid="save-quote-btn" onClick={save} disabled={saving || items.length === 0}
        className="w-full bg-[#FFD600] text-[#0A0A0A] h-16 text-xl font-black uppercase rounded-md hover:bg-[#E6C200] transition disabled:opacity-50 flex items-center justify-center gap-2">
        <FileText size={24} weight="bold" /> {saving ? "Salvataggio…" : (isEdit ? "Salva modifiche" : "Crea Preventivo")}
      </button>

      {showPicker && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-2" onClick={()=>setShowPicker(false)}>
          <div className="bg-[#1A1A1A] border border-[#333] rounded-md w-full max-w-lg max-h-[80vh] overflow-auto" onClick={(e)=>e.stopPropagation()}>
            <div className="sticky top-0 bg-[#1A1A1A] border-b border-[#333] p-4 flex justify-between">
              <div className="font-heading text-xl font-bold uppercase">Aggiungi voce</div>
              <button onClick={()=>setShowPicker(false)} className="text-[#A3A3A3]" data-testid="close-picker"><Check size={24} weight="bold"/></button>
            </div>
            <div className="p-2">
              {pricelist.map((p) => (
                <button key={p.code} data-testid={`pick-${p.code}`}
                  onClick={()=>{ addManual(p); setShowPicker(false); }}
                  className="w-full text-left p-3 hover:bg-[#0A0A0A] rounded-md border-b border-[#333]">
                  <div className="font-mono text-xs text-[#FFD600]">{p.code}</div>
                  <div className="text-sm font-bold">{p.description}</div>
                  <div className="text-xs text-[#A3A3A3]">€ {p.price} / {p.unit}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

